import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Popover,
  Grid,
  SxProps,
  Theme,
} from '@mui/material';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  minRows?: number;
  sx?: SxProps<Theme>;
}

const EMOJIS = [
  '😀',
  '😂',
  '😍',
  '😎',
  '😊',
  '👍',
  '🙏',
  '💪',
  '🔥',
  '🎉',
  '🤔',
  '😉',
  '😢',
  '😡',
  '🥳',
  '💖',
  '🌟',
  '🍕',
  '🍔',
  '☕',
  '🍰',
  '🧁',
  '🍩',
  '🍫',
  '🍬',
  '🍪',
  '🍭',
  '🍎',
  '🍓',
  '🍇',
  '🍉',
  '🍌',
  '🍒',
  '✨',
  '⭐',
  '🌠',
  '🇺🇦',
  '👋🏻',
  '🥧',
  '🍯',
  '🍦',
  '🍨',
  '🍧',
  '🥮',
  '🍮',
  '🫓',
  '🥪',
  '🥗',
  '🍖',
  '🍗',
  '🥩',
  '🥞',
  '🧇',
  '🥟',
  '🍤',
  '🍱',
  '🍣',
  '🍛',
  '🍜',
  '🥫',
  '🍲',
  '🍿',
  '🧈',
  '🧂',
  '🥛',
  '🍼',
  '🍵',
  '🍶',
];

export function RichTextEditor({
  value,
  onChange,
  label,
  minRows = 4,
  sx,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [selectionRange, setSelectionRange] = useState<Range | null>(null);

  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const triggerChange = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const format = (command: string, val?: string) => {
    document.execCommand(command, false, val);
    triggerChange();
  };

  const handleEmojiButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setEmojiAnchor(event.currentTarget);
  };

  const handleEmojiClick = (emoji: string) => {
    format('insertText', emoji);
    setEmojiAnchor(null);
  };

  const handleLinkClick = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) setSelectionRange(sel.getRangeAt(0));
    setLinkDialogOpen(true);
  };

  const insertLink = () => {
    if (!selectionRange) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(selectionRange);
    document.execCommand('createLink', false, linkUrl);
    triggerChange();
    setLinkDialogOpen(false);
    setLinkUrl('');
  };

  return (
    <Box sx={sx}>
      {label && <Typography sx={{ mb: 1 }}>{label}</Typography>}

      <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">
        <Button size="small" variant="outlined" onClick={() => format('bold')}>
          B
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => format('italic')}
        >
          I
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => format('underline')}
        >
          U
        </Button>
        <Button size="small" variant="outlined" onClick={handleLinkClick}>
          Посилання
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={handleEmojiButtonClick}
        >
          😃
        </Button>
      </Stack>

      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1 }}>
          <Grid container spacing={0.5}>
            {EMOJIS.map((emoji) => (
              <Grid size={{ xs: 0.5 }} key={emoji}>
                <Button
                  onClick={() => handleEmojiClick(emoji)}
                  sx={{ minWidth: 0, padding: 0.5, fontSize: 20 }}
                >
                  {emoji}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>

      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={triggerChange}
        sx={{
          border: '1px solid #ccc',
          borderRadius: 1,
          p: 1,
          minHeight: minRows * 24,
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
        }}
      />

      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)}>
        <DialogTitle>Вставити посилання</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Відмінити</Button>
          <Button variant="contained" onClick={insertLink}>
            Вставити
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
